import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmt, isZero } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { AppHeader } from "@/components/AppHeader";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const monthLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
export const monthRange = (m) => {
  const last = new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)), 0).getDate();
  return `start=${m}-01&end=${m}-${String(last).padStart(2, "0")}`;
};

const DAILY = ["Deposit", "Withdrawal", "Cashout", "Bonus", "Difference", "Closing Avail", "Entries"];
const SETT = ["Sett Deposit", "Sett Withdrawal", "Sett Difference", "Sett Closing"];
const n = (v) => (v === undefined ? "—" : fmt(v));
const Pnl = ({ v, testId }) => v === undefined ? <td className="px-3 py-2.5 text-right text-slate-400">—</td> : (
  <td data-testid={testId} className={`px-3 py-2.5 text-right font-mono tabular-nums font-semibold ${isZero(v) ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{fmt(v)}</td>
);
const Num = ({ v, strong }) => <td className={`px-3 py-2.5 text-right font-mono tabular-nums ${strong ? "font-semibold" : ""}`}>{n(v)}</td>;

const MonthRow = ({ b, m, nav }) => {
  const s = m.sett || {};
  const { can } = useAuth();
  return (
    <tr onClick={() => nav(`/book/${b.book_id}?${monthRange(m.month)}`)} title="Open this month in the sheet"
      className="border-t border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors" data-testid={`summary-month-${b.book_id}-${m.month}`}>
      <td className="px-3 py-2.5 font-semibold whitespace-nowrap underline decoration-slate-300 underline-offset-4">{monthLabel(m.month)}</td>
      <Num v={m.deposit} /><Num v={m.withdrawal} /><Num v={m.cashout} /><Num v={m.bonus} />
      <Pnl v={m.pnl} testId="summary-month-pnl" />
      <Num v={m.closing_available} strong />
      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-500">{m.entries}{m.mismatches ? <span className="text-red-600"> · {m.mismatches}≠</span> : ""}</td>
      {b.has_sett && <>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums bg-amber-50/60 border-l-2 border-amber-400">{n(s.deposit)}</td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums bg-amber-50/60">{n(s.withdrawal)}</td>
        <Pnl v={s.pnl} testId="summary-month-sett-pnl" />
        <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold bg-amber-50/60" data-testid="summary-month-sett-closing">{n(s.closing_available)}</td>
      </>}
      <td className="px-2 py-2.5 text-center">
        {can("export") && <Link to={`/book/${b.book_id}/print?month=${m.month}`} onClick={(e) => e.stopPropagation()} title="Printable page"
          data-testid={`summary-print-${b.book_id}-${m.month}`} className="inline-grid place-items-center h-8 w-8 hover:bg-slate-900 hover:text-white transition-colors">
          <Printer size={15} />
        </Link>}
      </td>
    </tr>
  );
};

const SUM = ["deposit", "withdrawal", "cashout", "bonus", "pnl", "entries", "mismatches"];
const yearTotal = (months, pick) => {
  const list = months.map(pick).filter((x) => x && x.deposit !== undefined);
  if (!list.length) return undefined;
  const t = Object.fromEntries(SUM.map((k) => [k, list.reduce((a, m) => a + m[k], 0)]));
  return { ...t, closing_available: list[0].closing_available };
};

const YearRow = ({ b, year, months }) => {
  const d = yearTotal(months, (m) => m) || {};
  const s = yearTotal(months, (m) => m.sett) || {};
  return (
    <tr className="bg-slate-900 text-white font-semibold" data-testid={`summary-year-${b.book_id}-${year}`}>
      <td className="px-3 py-2.5 whitespace-nowrap uppercase tracking-[0.1em] text-xs">{year} total</td>
      {["deposit", "withdrawal", "cashout", "bonus"].map((k) => <td key={k} className="px-3 py-2.5 text-right font-mono tabular-nums">{n(d[k])}</td>)}
      <td data-testid="summary-year-pnl" className={`px-3 py-2.5 text-right font-mono tabular-nums ${d.pnl === undefined ? "" : isZero(d.pnl) ? "text-green-300" : "text-red-300"}`}>{n(d.pnl)}</td>
      <td className="px-3 py-2.5 text-right font-mono tabular-nums" data-testid="summary-year-closing">{n(d.closing_available)}</td>
      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-white/70">{d.entries ?? 0}{d.mismatches ? <span className="text-red-300"> · {d.mismatches}≠</span> : ""}</td>
      {b.has_sett && <>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums border-l-2 border-amber-400 text-amber-200">{n(s.deposit)}</td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-amber-200">{n(s.withdrawal)}</td>
        <td data-testid="summary-year-sett-pnl" className={`px-3 py-2.5 text-right font-mono tabular-nums ${s.pnl === undefined ? "" : isZero(s.pnl) ? "text-green-300" : "text-red-300"}`}>{n(s.pnl)}</td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-amber-200">{n(s.closing_available)}</td>
      </>}
      <td />
    </tr>
  );
};

const BookBody = ({ b, nav }) => {
  const years = [...new Set(b.months.map((m) => m.month.slice(0, 4)))];
  return years.map((y) => {
    const ms = b.months.filter((m) => m.month.startsWith(y));
    return (
      <tbody key={y}>
        {ms.map((m) => <MonthRow key={m.month} b={b} m={m} nav={nav} />)}
        <YearRow b={b} year={y} months={ms} />
      </tbody>
    );
  });
};

const BookMonths = ({ b, nav }) => (
  <section className="bg-white border border-slate-300 fade-up" data-testid={`summary-book-${b.book_id}`}>
    <div className="flex items-center justify-between px-4 py-3 border-b-2 border-slate-900">
      <h2 className="font-extrabold uppercase tracking-tight text-base md:text-lg">{b.name}</h2>
      <Link to={`/book/${b.book_id}`} className="text-xs font-semibold underline underline-offset-2" data-testid={`summary-open-${b.book_id}`}>Open sheet</Link>
    </div>
    {b.months.length === 0 ? <p className="px-4 py-4 text-sm text-slate-500">No entries yet.</p> : (
      <div className="ledger-scroll overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="text-[10px] uppercase tracking-[0.12em] text-slate-500 bg-slate-50">
            <tr>
              <th className="px-3 py-2 font-bold text-left">Month</th>
              {DAILY.map((h) => <th key={h} className="px-3 py-2 font-bold text-right whitespace-nowrap">{h}</th>)}
              {b.has_sett && SETT.map((h, i) => <th key={h} className={`px-3 py-2 font-bold text-right whitespace-nowrap bg-amber-100 text-amber-900 ${i === 0 ? "border-l-2 border-amber-400" : ""}`}>{h}</th>)}
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <BookBody b={b} nav={nav} />
        </table>
      </div>
    )}
  </section>
);

export default function SummaryPage() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/summary/monthly").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  return (
    <div className="min-h-screen grid-paper">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Month by month · tap a month to open it</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Monthly summary</h1>
        </div>
        {!data ? <p className="text-sm text-slate-500" data-testid="summary-loading">Loading…</p>
          : data.length === 0 ? <p className="text-sm text-slate-500" data-testid="summary-empty">No books yet.</p>
          : data.map((b) => <BookMonths key={b.book_id} b={b} nav={nav} />)}
      </main>
    </div>
  );
}
